<template>
  <div class="generated-dashboard-wrapper" :style="themeStyles">
    <!-- Custom Dashboard Header (Uses Selected Theme Colors) -->
    <header class="custom-header">
      <div class="container-fluid">
        <div class="header-content">
          <div class="header-brand">
            <img
              v-if="config.logo"
              :src="config.logo"
              alt="Logo"
              class="brand-logo"
            />
            <div class="brand-text">
              <h1 class="brand-title">{{ config.name || 'Custom Dashboard' }}</h1>
              <p class="brand-subtitle">{{ config.description || 'Data Analytics Platform' }}</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Profile dropdown if author -->
            <b-dropdown
              v-if="isAuthor"
              right
              variant="link"
              toggle-class="text-decoration-none p-0 mr-3 border-0 d-flex align-items-center"
              no-caret
            >
              <template #button-content>
                <div class="profile-circle-dash bg-white text-primary rounded-circle d-flex justify-content-center align-items-center">
                  <span class="font-weight-bold">{{ userInitials }}</span>
                </div>
              </template>
              <div class="dropdown-header text-center pb-2 border-bottom">
                <strong>{{ currentUser?.email }}</strong>
              </div>
              <b-dropdown-item @click="goToProfile" class="mt-2">
                <i class="fas fa-th-large mr-2 text-primary"></i> My Dashboards
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="handleLogout" class="text-danger">
                <i class="fas fa-sign-out-alt mr-2"></i> Logout
              </b-dropdown-item>
            </b-dropdown>

            <!-- CTA if not author or visitor -->
            <b-button
              v-else
              variant="outline-light"
              size="sm"
              class="mr-3 custom-cta-btn"
              @click="buildOwnDashboard"
            >
              <i class="fas fa-magic mr-1"></i>
              Build Your Own Dashboard
            </b-button>

            <!-- Export Button -->
            <b-button
              v-if="config?.settings?.showExportButton !== false"
              :style="{ backgroundColor: appearance.colors?.accent || '#28a745', borderColor: appearance.colors?.accent || '#28a745' }"
              size="sm"
              @click="exportData"
            >
              <i class="fas fa-download mr-1"></i>
              Export
            </b-button>
          </div>
        </div>
      </div>
    </header>

    <!-- Filter Control Panel (Above Dashboard) -->
    <div class="filter-panel" v-if="config?.settings?.showFilters !== false">
      <div class="container-fluid">
        <div class="filter-row">
          <div class="filter-item">
            <label class="filter-label">Indicator(s)</label>
            <b-form-select
              v-model="selectedIndicator"
              :options="indicatorOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">Location</label>
            <b-form-select
              v-model="selectedLocation"
              :options="locationOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">Data Source</label>
            <b-form-select
              v-model="selectedDatasource"
              :options="datasourceOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">Period</label>
            <b-form-select
              v-model="selectedPeriod"
              :options="periodOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </div>

        </div>
      </div>
    </div>

    <!-- Main Dashboard Layout -->
    <div class="dashboard-main">
      <!-- Left Sidebar - Section Tabs & Stats -->
      <aside class="dashboard-sidebar" :class="{ 'collapsed': sidebarCollapsed }">
        <!-- Section Navigation Tabs -->
        <div class="section-nav">
          <ul class="section-list">
            <li
              v-for="section in dashboardSections"
              :key="section.index"
              class="section-item"
              :class="{ 'active': activeSection === section.index }"
              @click="setActiveSection(section.index)"
            >
              <i :class="`fas fa-${section.icon}`" class="section-icon"></i>
              <span class="section-text">{{ section.title }}</span>
            </li>
          </ul>
        </div>

        <!-- Section Title Bar -->
        <div class="section-title-bar">
          <h6 class="section-title">{{ currentSectionTitle }}</h6>
        </div>

        <!-- Clickable Stats Summary -->
        <div class="stats-panel" v-if="config?.settings?.showSummaryCards !== false">
          <div
            class="stat-card"
            :class="{ 'active': activeStatFilter === 'indicators' }"
            @click="filterByStat('indicators')"
          >
            <div class="stat-icon-wrapper primary">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="stat-details">
              <span class="stat-number">{{ dataSummary.indicatorCount || 0 }}</span>
              <span class="stat-name">Indicators</span>
            </div>
          </div>

          <div
            class="stat-card"
            :class="{ 'active': activeStatFilter === 'locations' }"
            @click="filterByStat('locations')"
          >
            <div class="stat-icon-wrapper success">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <div class="stat-details">
              <span class="stat-number">{{ dataSummary.locationCount || 0 }}</span>
              <span class="stat-name">Locations</span>
            </div>
          </div>

          <div
            class="stat-card"
            :class="{ 'active': activeStatFilter === 'periods' }"
            @click="filterByStat('periods')"
          >
            <div class="stat-icon-wrapper info">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="stat-details">
              <span class="stat-number">{{ dataSummary.periodCount || 0 }}</span>
              <span class="stat-name">Periods</span>
            </div>
          </div>

          <div
            class="stat-card"
            :class="{ 'active': activeStatFilter === 'datasources' }"
            @click="filterByStat('datasources')"
          >
            <div class="stat-icon-wrapper warning">
              <i class="fas fa-database"></i>
            </div>
            <div class="stat-details">
              <span class="stat-number">{{ dataSummary.datasourceCount || 0 }}</span>
              <span class="stat-name">Sources</span>
            </div>
          </div>

          <div class="stat-card total">
            <div class="stat-icon-wrapper secondary">
              <i class="fas fa-list"></i>
            </div>
            <div class="stat-details">
              <span class="stat-number">{{ dataSummary.totalRows || 0 }}</span>
              <span class="stat-name">Data Points</span>
            </div>
          </div>
        </div>

        <!-- Sidebar Collapse Toggle -->
        <button class="collapse-toggle" @click="sidebarCollapsed = !sidebarCollapsed">
          <i :class="sidebarCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
        </button>
      </aside>

      <!-- Main Content Area -->
      <main class="dashboard-content">
        <div class="content-wrapper">
          <!-- No Data State -->
          <div v-if="!hasData" class="empty-state">
            <div class="empty-content">
              <i class="fas fa-database fa-4x"></i>
              <h4>No Data Available</h4>
              <p>Please configure your dashboard and upload data to see visualizations.</p>
              <b-button variant="primary" @click="isAuthor ? goToProfile() : buildOwnDashboard()">
                <i class="fas fa-upload mr-2"></i>
                Upload Data
              </b-button>
            </div>
          </div>

          <!-- Dashboard Sections -->
          <div v-else class="sections-wrapper">
            <!-- Section 0: Indicator Overview (8col + 4col layout) -->
            <section v-show="activeSection === 0" class="content-section">
              <div class="section-card">
                <div class="section-header">
                  <h5 class="title">Indicator Overview</h5>
                  <div class="header-tools">
                    <img
                      src="@/assets/Expand.png"
                      alt="Expand"
                      class="tool-icon"
                      @click="expandSection(0)"
                    />
                  </div>
                </div>
                <div class="section-body">
                  <div class="row">
                    <!-- Left Column (8 cols) -->
                    <div class="col-md-8">
                      <div class="row">
                        <!-- Data Summary Table -->
                        <div class="col-md-12 mb-4">
                          <div class="chart-card">
                            <div class="chart-header">
                              <span class="chart-title">Data Summary — {{ filterContext }}</span>
                            </div>
                            <div class="chart-body">
                              <b-table
                                :items="tableData"
                                :fields="summaryTableFields"
                                :per-page="5"
                                :current-page="tablePage"
                                small
                                hover
                                responsive
                                class="data-table"
                              >
                                <template #cell(value)="data">
                                  <span class="value-highlight">{{ formatValue(data.value) }}</span>
                                </template>
                              </b-table>
                              <b-pagination
                                v-if="tableData.length > 5"
                                v-model="tablePage"
                                :total-rows="tableData.length"
                                :per-page="5"
                                size="sm"
                                align="center"
                              ></b-pagination>
                            </div>
                          </div>
                        </div>

                        <!-- Indicator Comparison Chart -->
                        <div class="col-md-12">
                          <div class="chart-card">
                            <div class="chart-header">
                              <span class="chart-title">{{ selectedIndicator || 'All Indicators' }} — Data Source Comparison{{ selectedLocation ? ' · ' + selectedLocation : '' }}</span>
                            </div>
                            <div class="chart-body">
                              <div class="chart-container">
                                <highcharts :options="indicatorComparisonChart"></highcharts>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Right Column (4 cols) - Location Bar Chart -->
                    <div class="col-md-4">
                      <div class="chart-card stretch">
                        <div class="chart-header">
                          <span class="chart-title">Values by Location — {{ selectedIndicator || 'All Indicators' }}{{ selectedDatasource ? ' · ' + selectedDatasource : '' }}</span>
                        </div>
                        <div class="chart-body">
                          <div class="chart-container tall">
                            <highcharts :options="locationBarChart"></highcharts>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 1: Trend Analysis -->
            <section v-show="activeSection === 1" class="content-section">
              <div class="section-card">
                <div class="section-header">
                  <h5 class="title">Trend Analysis</h5>
                </div>
                <div class="section-body">
                  <div class="row">
                    <div class="col-md-12">
                      <div class="chart-card">
                        <div class="chart-header">
                          <span class="chart-title">{{ selectedIndicator || 'All Indicators' }} — Trend Over Time{{ selectedDatasource ? ' · ' + selectedDatasource : '' }}</span>
                        </div>
                        <div class="chart-body">
                          <div class="chart-container large">
                            <highcharts :options="trendLineChart"></highcharts>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 2: Location Comparison -->
            <section v-show="activeSection === 2" class="content-section">
              <div class="section-card">
                <div class="section-header">
                  <h5 class="title">Location Comparison</h5>
                </div>
                <div class="section-body">
                  <div class="row">
                    <div class="col-md-8">
                      <div class="chart-card">
                        <div class="chart-header">
                          <span class="chart-title">Location Performance — {{ selectedIndicator || 'All Indicators' }}</span>
                        </div>
                        <div class="chart-body">
                          <div class="chart-container">
                            <highcharts :options="locationComparisonChart"></highcharts>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="chart-card stretch">
                        <div class="chart-header">
                          <span class="chart-title">Distribution by Source{{ selectedIndicator ? ' — ' + selectedIndicator : '' }}{{ selectedLocation ? ' · ' + selectedLocation : '' }}</span>
                        </div>
                        <div class="chart-body">
                          <div class="chart-container">
                            <highcharts :options="datasourcePieChart"></highcharts>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 3: Data Explorer -->
            <section v-show="activeSection === 3" class="content-section">
              <div class="section-card">
                <div class="section-header">
                  <h5 class="title">Data Explorer</h5>
                  <div class="header-tools">
                    <b-button v-if="config?.settings?.showExportButton !== false" variant="outline-light" size="sm" @click="exportData">
                      <i class="fas fa-download mr-1"></i>
                      Export CSV
                    </b-button>
                  </div>
                </div>
                <div class="section-body">
                  <div class="row">
                    <div class="col-md-12">
                      <div class="chart-card">
                        <div class="chart-body">
                          <b-table
                            :items="transformedData"
                            :fields="tableFields"
                            :per-page="perPage"
                            :current-page="currentPage"
                            striped
                            hover
                            responsive
                            small
                            show-empty
                            class="data-table"
                          >
                            <template #empty>
                              <div class="text-center text-muted py-3">
                                No data matching current filters
                              </div>
                            </template>
                            <template #cell(value)="data">
                              <span class="value-highlight">{{ formatValue(data.value) }}</span>
                            </template>
                          </b-table>

                          <b-pagination
                            v-model="currentPage"
                            :total-rows="transformedData ? transformedData.length : 0"
                            :per-page="perPage"
                            align="center"
                            size="sm"
                            class="mt-3"
                          ></b-pagination>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>

    <!-- Custom Dashboard Footer (Uses Selected Theme Colors) -->
    <footer class="custom-footer">
      <div class="container-fluid">
        <div class="footer-content">
          <div class="footer-brand">
            <span class="footer-name">{{ config.name || 'Custom Dashboard' }}</span>
            <span class="footer-divider">•</span>
            <span class="footer-tagline">Powered by MSDAT Configurer</span>
          </div>
          <div class="footer-info">
            <span class="footer-records">{{ filteredData.length }} records</span>
            <span class="footer-divider">•</span>
            <span class="footer-date">{{ currentDate }}</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { filterData, aggregateForChart, groupDataBy } from '../../utils/dataTransformer';

export default {
  name: 'DynamicDashboard',

  data() {
    return {
      // Sidebar state
      sidebarCollapsed: false,
      activeSection: 0,
      activeStatFilter: null,

      // Filters
      selectedIndicator: null,
      selectedLocation: null,
      selectedPeriod: null,
      selectedDatasource: null,

      // Table pagination
      currentPage: 1,
      tablePage: 1,
      perPage: 10,

      // Table fields
      tableFields: [
        { key: 'indicator', label: 'Indicator', sortable: true },
        { key: 'location', label: 'Location', sortable: true },
        { key: 'period', label: 'Period', sortable: true },
        { key: 'value', label: 'Value', sortable: true },
        { key: 'datasource', label: 'Data Source', sortable: true },
      ],

      // Summary table fields
      summaryTableFields: [
        { key: 'location', label: 'Location', sortable: true },
        { key: 'value', label: 'Value', sortable: true },
        { key: 'period', label: 'Period', sortable: true },
        { key: 'datasource', label: 'Source', sortable: true },
      ],
    };
  },

  computed: {
    ...mapState('configurer', [
      'dashboardConfig',
      'appearanceConfig',
      'transformedData',
      'dataSummary',
      'instances',
      'activeInstanceId',
    ]),
    ...mapState('configurerAuth', {
      currentUser: (state) => state.user,
    }),

    currentInstance() {
      const instanceId = this.$route.params.instanceId;
      if (instanceId && instanceId !== 'preview') {
        return this.instances.find((i) => i.id === instanceId);
      }
      return null;
    },

    isAuthor() {
      if (this.$route.params.instanceId === 'preview') return true;
      if (this.currentUser && this.currentInstance) {
        const ownerId = this.currentInstance.ownerId || this.currentInstance.userId || this.currentInstance.user;
        if (this.currentUser.uid === ownerId) return true;
      }
      return false;
    },

    userInitials() {
      if (this.currentUser && this.currentUser.email) {
        return this.currentUser.email.substring(0, 2).toUpperCase();
      }
      return 'U';
    },

    filterContext() {
      const parts = [];
      parts.push(this.selectedIndicator || 'All Indicators');
      if (this.selectedLocation) parts.push(this.selectedLocation);
      if (this.selectedDatasource) parts.push(this.selectedDatasource);
      return parts.join(' · ');
    },

    dashboardSections() {
      const sections = [];
      sections.push({ title: 'Indicator Overview', icon: 'chart-bar', index: 0 });
      if (this.config?.settings?.showTrendAnalysis !== false) {
        sections.push({ title: 'Trend Analysis', icon: 'chart-line', index: 1 });
      }
      sections.push({ title: 'Location Comparison', icon: 'map', index: 2 });
      if (this.config?.settings?.showDataTable !== false) {
        sections.push({ title: 'Data Explorer', icon: 'table', index: 3 });
      }
      return sections;
    },

    // Get config - either from store or from loaded instance
    config() {
      return this.dashboardConfig || {};
    },

    // Get appearance config
    appearance() {
      return this.appearanceConfig || {};
    },

    // Current section title
    currentSectionTitle() {
      return this.dashboardSections[this.activeSection]?.title || 'Dashboard';
    },

    // Theme styles based on appearance config (used by header, footer, and components)
    themeStyles() {
      const colors = this.appearance.colors || {};
      return {
        '--primary-color': colors.primary || '#007bff',
        '--secondary-color': colors.secondary || '#6c757d',
        '--accent-color': colors.accent || '#28a745',
        '--bg-color': colors.background || '#f8f9fa',
        '--text-color': colors.text || '#212529',
        '--header-bg': colors.primary || '#2c3e50',
        '--footer-bg': colors.primary || '#2c3e50',
      };
    },

    // Check if we have data
    hasData() {
      return this.transformedData && this.transformedData.length > 0;
    },

    // Filter options
    indicatorOptions() {
      if (!this.transformedData) return [{ value: null, text: 'All Indicators' }];
      const indicators = [...new Set(this.transformedData.map((r) => r.indicator))];
      return [{ value: null, text: 'All Indicators' }, ...indicators.map((i) => ({ value: i, text: i }))];
    },

    locationOptions() {
      if (!this.transformedData) return [{ value: null, text: 'All Locations' }];
      const locations = [...new Set(this.transformedData.map((r) => r.location))];
      return [{ value: null, text: 'All Locations' }, ...locations.map((l) => ({ value: l, text: l }))];
    },

    periodOptions() {
      if (!this.transformedData) return [{ value: null, text: 'All Periods' }];
      const periods = [...new Set(this.transformedData.map((r) => r.period))].sort();
      return [{ value: null, text: 'All Periods' }, ...periods.map((p) => ({ value: p, text: p }))];
    },

    datasourceOptions() {
      if (!this.transformedData) return [{ value: null, text: 'All Sources' }];
      const sources = [...new Set(this.transformedData.map((r) => r.datasource))];
      return [{ value: null, text: 'All Sources' }, ...sources.map((s) => ({ value: s, text: s }))];
    },

    // Filtered data based on selections
    filteredData() {
      if (!this.transformedData) return [];
      return filterData(this.transformedData, {
        indicators: this.selectedIndicator ? [this.selectedIndicator] : null,
        locations: this.selectedLocation ? [this.selectedLocation] : null,
        periods: this.selectedPeriod ? [this.selectedPeriod] : null,
        datasources: this.selectedDatasource ? [this.selectedDatasource] : null,
      });
    },

    // Trend data (same as filteredData but ignores the period filter)
    trendData() {
      if (!this.transformedData) return [];
      return filterData(this.transformedData, {
        indicators: this.selectedIndicator ? [this.selectedIndicator] : null,
        locations: this.selectedLocation ? [this.selectedLocation] : null,
        datasources: this.selectedDatasource ? [this.selectedDatasource] : null,
        // intentionally omitting periods filter
      });
    },

    // Table data for summary table
    tableData() {
      return this.filteredData.slice(0, 50);
    },

    // Current date for footer
    currentDate() {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    // Chart: Indicator Comparison (like IDCC)
    indicatorComparisonChart() {
      const grouped = groupDataBy(this.filteredData, 'datasource');
      const datasources = Object.keys(grouped);

      const data = datasources.map((ds) => {
        const rows = grouped[ds];
        const avg = rows.reduce((sum, r) => sum + r.value, 0) / rows.length;
        return Math.round(avg * 100) / 100;
      });

      return {
        chart: { type: 'column', height: 280 },
        title: { text: '' },
        xAxis: {
          categories: datasources,
          labels: { rotation: -45, style: { fontSize: '11px' } },
        },
        yAxis: { title: { text: 'Average Value' } },
        series: [{
          name: 'Average Value',
          data,
          color: this.appearance.colors?.primary || '#007bff',
        }],
        legend: { enabled: false },
        credits: { enabled: false },
      };
    },

    // Chart: Location Bar Chart
    locationBarChart() {
      const aggregated = aggregateForChart(this.filteredData, {
        groupBy: 'location',
        aggregation: 'avg',
      });

      return {
        chart: { type: 'bar', height: 500 },
        title: { text: '' },
        xAxis: {
          categories: aggregated.map((a) => a.name),
          labels: { style: { fontSize: '11px' } },
        },
        yAxis: { title: { text: 'Value' } },
        plotOptions: {
          bar: { dataLabels: { enabled: true, format: '{y:.1f}' } },
        },
        series: [{
          name: 'Value',
          data: aggregated.map((a) => a.value),
          color: this.appearance.colors?.accent || '#28a745',
        }],
        legend: { enabled: false },
        credits: { enabled: false },
      };
    },

    // Chart: Location Comparison
    locationComparisonChart() {
      const aggregated = aggregateForChart(this.filteredData, {
        groupBy: 'location',
        aggregation: 'avg',
      });

      return {
        chart: { type: 'column', height: 350 },
        title: { text: '' },
        xAxis: {
          categories: aggregated.map((a) => a.name),
          labels: { rotation: -45, style: { fontSize: '10px' } },
        },
        yAxis: { title: { text: 'Average Value' } },
        series: [{
          name: 'Average Value',
          data: aggregated.map((a) => a.value),
          color: this.appearance.colors?.primary || '#007bff',
        }],
        legend: { enabled: false },
        credits: { enabled: false },
      };
    },

    // Chart: Datasource Pie Chart
    datasourcePieChart() {
      const grouped = groupDataBy(this.filteredData, 'datasource');
      const data = Object.entries(grouped).map(([name, rows]) => ({
        name,
        y: rows.length,
      }));

      return {
        chart: { type: 'pie', height: 350 },
        title: { text: '' },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f}%',
              style: { fontSize: '10px' },
            },
          },
        },
        series: [{ name: 'Records', data }],
        credits: { enabled: false },
      };
    },

    // Chart: Trend Line Chart
    trendLineChart() {
      // Use trendData instead of filteredData so it ignores the selected year
      const grouped = groupDataBy(this.trendData, 'period');
      const periods = Object.keys(grouped).sort();
      const indicators = [...new Set(this.trendData.map((r) => r.indicator))].slice(0, 5);

      const series = indicators.map((indicator) => ({
        name: indicator,
        data: periods.map((period) => {
          const rows = grouped[period]?.filter((r) => r.indicator === indicator) || [];
          if (rows.length === 0) return null;
          return Math.round((rows.reduce((sum, r) => sum + r.value, 0) / rows.length) * 100) / 100;
        }),
      }));

      return {
        chart: { type: 'line', height: 400 },
        title: { text: '' },
        xAxis: { categories: periods },
        yAxis: { title: { text: 'Value' } },
        series,
        legend: {
          enabled: true,
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom',
        },
        credits: { enabled: false },
      };
    },
  },

  async mounted() {
    // Load instance if ID provided
    const instanceId = this.$route.params.instanceId;
    if (instanceId && instanceId !== 'preview') {
      await this.loadInstance(instanceId);
    }

    // Set browser tab title to dashboard name
    const dashName = this.config?.name;
    if (dashName) {
      document.title = dashName;
    }

    // Apply default filter settings
    if (this.config && this.config.settings) {
      const { settings } = this.config;
      this.selectedIndicator = settings.defaultIndicator || null;
      this.selectedLocation = settings.defaultLocation || null;
      this.selectedPeriod = settings.defaultPeriod || null;
      this.selectedDatasource = settings.defaultDatasource || null;
    }
  },

  methods: {
    ...mapActions('configurer', ['loadInstance']),
    ...mapActions('configurerAuth', {
      logoutAction: 'logout',
    }),

    async handleLogout() {
      try {
        await this.logoutAction();
        this.$router.push('/configure/account');
      } catch (error) {
        console.error(error);
      }
    },

    goToProfile() {
      this.$router.push('/configure/user/profile');
    },

    buildOwnDashboard() {
      this.$router.push('/configure/account');
    },

    setActiveSection(index) {
      this.activeSection = index;
    },

    filterByStat(statType) {
      if (this.activeStatFilter === statType) {
        this.activeStatFilter = null;
        this.resetFilters();
      } else {
        this.activeStatFilter = statType;
        switch (statType) {
          case 'indicators': this.activeSection = 0; break;
          case 'locations': this.activeSection = 2; break;
          case 'periods': this.activeSection = 1; break;
          case 'datasources': this.activeSection = 0; break;
          default: break;
        }
      }
    },

    resetFilters() {
      this.selectedIndicator = null;
      this.selectedLocation = null;
      this.selectedPeriod = null;
      this.selectedDatasource = null;
      this.activeStatFilter = null;
    },

    expandSection(index) {
      console.log('Expanding section:', index);
    },

    formatValue(value) {
      if (typeof value === 'number') {
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
      }
      return value;
    },

    exportData() {
      const headers = ['Indicator', 'Location', 'Period', 'Value', 'Data Source'];
      const rows = this.transformedData.map((row) => [
        row.indicator,
        row.location,
        row.period,
        row.value,
        row.datasource,
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.config.name || 'dashboard'}_data.csv`;
      link.click();
    },
  },
};
</script>

<style scoped>
/* ===== Root Wrapper - Standalone Dashboard ===== */
.generated-dashboard-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-color, #f8f9fa);
  color: var(--text-color, #212529);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  overflow: hidden;
}

/* ===== Custom Header (Uses Theme Colors) ===== */
.custom-header {
  background: linear-gradient(135deg, var(--header-bg, #2c3e50) 0%, color-mix(in srgb, var(--header-bg, #2c3e50) 70%, black) 100%);
  color: white;
  padding: 0.875rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-brand {
  display: flex;
  align-items: center;
}

.brand-logo {
  height: 42px;
  margin-right: 1rem;
}

.brand-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0;
  line-height: 1.2;
}

.brand-subtitle {
  font-size: 0.8rem;
  opacity: 0.85;
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

/* ===== Filter Panel (Above Dashboard) ===== */
.filter-panel {
  background: white;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-item {
  flex: 1;
  min-width: 150px;
  max-width: 220px;
}

.filter-item.filter-actions {
  flex: 0 0 auto;
  min-width: auto;
}

.filter-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.25rem;
  letter-spacing: 0.5px;
}

.filter-select {
  width: 100%;
  font-size: 0.85rem;
}

.reset-btn {
  white-space: nowrap;
}

/* ===== Dashboard Main Layout ===== */
.dashboard-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ===== Sidebar ===== */
.dashboard-sidebar {
  width: 260px;
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: width 0.3s ease;
  flex-shrink: 0;
  overflow-y: auto;
}

.dashboard-sidebar.collapsed {
  width: 60px;
}

.dashboard-sidebar.collapsed .section-text,
.dashboard-sidebar.collapsed .stat-details,
.dashboard-sidebar.collapsed .section-title {
  display: none;
}

/* Section Navigation */
.section-nav {
  border-bottom: 1px solid #e0e0e0;
}

.section-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.section-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
  color: #6c757d;
}

.section-item:hover {
  background: #f8f9fa;
  color: #495057;
}

.section-item.active {
  background: #f0f7ff;
  border-left-color: var(--primary-color, #007bff);
  color: var(--primary-color, #007bff);
  font-weight: 600;
}

.section-icon {
  width: 20px;
  margin-right: 0.75rem;
  font-size: 0.9rem;
}

.section-text {
  font-size: 0.85rem;
}

/* Section Title Bar */
.section-title-bar {
  padding: 0.6rem 1rem;
  background: var(--primary-color, #007bff);
  color: white;
}

.section-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

/* Stats Panel */
.stats-panel {
  padding: 0.75rem;
  flex: 1;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.6rem;
  margin-bottom: 0.35rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.stat-card:hover {
  background: #f5f7fa;
  border-color: #e0e4e8;
}

.stat-card.active {
  background: #e8f4fd;
  border-color: var(--primary-color, #007bff);
}

.stat-card.total {
  margin-top: 0.5rem;
  padding-top: 0.6rem;
  border-top: 1px solid #e8e8e8;
}

.stat-icon-wrapper {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.6rem;
  font-size: 0.8rem;
  color: white;
}

.stat-icon-wrapper.primary {
  background: linear-gradient(135deg, var(--primary-color, #007bff), #0056b3);
}

.stat-icon-wrapper.success {
  background: linear-gradient(135deg, #28a745, #1e7e34);
}

.stat-icon-wrapper.info {
  background: linear-gradient(135deg, #17a2b8, #117a8b);
}

.stat-icon-wrapper.warning {
  background: linear-gradient(135deg, #ffc107, #d39e00);
}

.stat-icon-wrapper.secondary {
  background: linear-gradient(135deg, #6c757d, #495057);
}

.stat-details {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-name {
  font-size: 0.65rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Collapse Toggle */
.collapse-toggle {
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 1px solid #d0d8e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  color: #6c757d;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.collapse-toggle:hover {
  background: #f8f9fa;
}

/* ===== Main Content ===== */
.dashboard-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
}

.content-wrapper {
  max-width: 100%;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.empty-content {
  text-align: center;
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.empty-content i {
  color: #adb5bd;
  margin-bottom: 1rem;
}

.empty-content h4 {
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.empty-content p {
  color: #adb5bd;
  margin-bottom: 1.5rem;
}

/* ===== Section Cards ===== */
.section-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.section-header {
  background: linear-gradient(135deg, var(--primary-color, #007bff) 0%, color-mix(in srgb, var(--primary-color, #007bff) 75%, black) 100%);
  color: white;
  padding: 0.7rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header .title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.header-tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tool-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: 0.9;
}

.tool-icon:hover {
  opacity: 1;
}

.section-body {
  padding: 1.25rem;
}

/* ===== Chart Cards ===== */
.chart-card {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
}

.chart-card.stretch {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-header {
  background: #f5f5f5;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #e8e8e8;
}

.chart-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.chart-body {
  padding: 1rem;
  flex: 1;
}

.chart-container {
  min-height: 280px;
}

.chart-container.tall {
  min-height: 480px;
}

.chart-container.large {
  min-height: 400px;
}

/* ===== Data Table ===== */
.data-table {
  font-size: 0.85rem;
}

.data-table th {
  background: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
}

.value-highlight {
  font-weight: 600;
  color: var(--primary-color, #007bff);
}

/* ===== Custom Footer (Uses Theme Colors) ===== */
.custom-footer {
  background: linear-gradient(135deg, var(--footer-bg, #2c3e50) 0%, color-mix(in srgb, var(--footer-bg, #2c3e50) 70%, black) 100%);
  color: white;
  padding: 0.65rem 0;
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-brand,
.footer-info {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
}

.footer-name {
  font-weight: 600;
}

.footer-divider {
  margin: 0 0.5rem;
  opacity: 0.6;
}

.footer-tagline,
.footer-records,
.footer-date {
  opacity: 0.85;
}

/* ===== Responsive ===== */
@media (max-width: 991px) {
  .dashboard-sidebar {
    width: 100%;
    position: relative;
    height: auto;
    max-height: 300px;
  }

  .dashboard-main {
    flex-direction: column;
  }

  .collapse-toggle {
    display: none;
  }

  .filter-row {
    flex-wrap: wrap;
  }

  .filter-item {
    flex: 1 1 45%;
    max-width: none;
  }
}

@media (max-width: 767px) {
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .header-brand {
    flex-direction: column;
  }

  .brand-logo {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }

  .filter-item {
    flex: 1 1 100%;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
}

.profile-circle-dash {
  width: 32px;
  height: 32px;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}
.profile-circle-dash:hover {
  transform: scale(1.05);
}
.custom-cta-btn {
  border-radius: 20px;
  font-weight: 500;
  letter-spacing: 0.5px;
}
</style>
